/**
 * SyncSummaryToastContent
 *
 * Rich body of the post-sync toast that surfaces the 4-counter import
 * resumen. Rendered inside `AppNotification.content` so the existing toast
 * shell (animation, dismiss button, progress bar) wraps it.
 *
 * Display rules (issue #322 B.3):
 *   - Always show "Imported" and "Skipped as duplicate" lines.
 *   - "Needs attention" line: ONLY when count > 0. Renders as a
 *     button-styled link to the tray (caller passes `onViewTray`).
 *   - "Not processable" line: ONLY when count > 0. Non-clickable info,
 *     "The team is working on it".
 *   - "Cancelled on the exchange" line: ONLY when count > 0. These rows were
 *     withheld ON PURPOSE (#692) — they are NOT failures, so they get their own
 *     line, their own glyph and copy that says nothing is missing. Lumping them
 *     into "Not processable" told the user to wait for a fix that will never
 *     come, and to re-upload a file hunting for data that never existed.
 */

import type { ImportJobSummary } from '@bat/shared/contracts/jobs/import-job.dto'
import { useTranslation } from 'react-i18next'

import { ChainHistoryIncompleteNotice } from './ChainHistoryIncompleteNotice'
import { ImportCoverageGapNotice } from './ImportCoverageGapNotice'
import { ImportSourceOverlapNotice } from './ImportSourceOverlapNotice'
import {
  isImportOutcomeFullyWithheld,
  resolveCancelledImportOutcome,
} from '../utils/import-outcome-message'

export interface SyncSummaryToastContentProps {
  summary: ImportJobSummary
  /** Called when the user clicks "Ver bandeja" — only rendered when there are unresolved warnings. */
  onViewTray: () => void
  /**
   * True when the import completed with `importedCount === 0` while other
   * counters (duplicates / needs-attention / unprocessable) are non-zero.
   * Renders a prominent warning headline ABOVE the 4 counters.
   */
  zeroImported?: boolean
}

export const SyncSummaryToastContent = ({
  summary,
  onViewTray,
  zeroImported,
}: SyncSummaryToastContentProps) => {
  const { t } = useTranslation('imports')

  // De qué endpoint venía cada fila descartada (#2186). Opcional en el
  // contrato: sólo la ruta API tiene endpoints, y los trabajos anteriores a
  // esta issue no lo traen — «ausente» no es «ninguno», así que sin él la
  // línea del agregado se pinta sola, exactamente como hasta ahora.
  const ignoredByRuleByEndpoint = summary.ignoredByRuleByEndpoint ?? []

  // A row the exchange reported as cancelled/failed was withheld ON PURPOSE (#692), so it is
  // counted and worded apart from the rows that genuinely could not be processed. The counts come
  // from the backend summary; the wording comes from the shared resolver the custom-importer
  // wizard uses, so the two surfaces cannot describe the same outcome differently.
  const cancelledCount = summary.cancelledCount ?? 0
  // Opcional en el contrato: los trabajos anteriores a #1457 no lo traen, y
  // «ausente» no es «ninguna» — por eso la línea sólo aparece con > 0.
  const importedWithWarningCount = summary.importedWithWarningCount ?? 0
  const outcomeCounts = {
    importedCount: summary.importedCount,
    cancelledCount,
    failedCount: summary.unprocessableCount,
    otherReportedCount: summary.skippedAsDuplicateCount + summary.needsAttentionCount,
  }
  const fullyWithheld = isImportOutcomeFullyWithheld(outcomeCounts)
  const withheldOutcome = fullyWithheld ? resolveCancelledImportOutcome(outcomeCounts) : null

  /**
   * ⚠️⚠️ NADA ENTRÓ **PORQUE YA ESTABA TODO**: eso no es una alarma.
   *
   * Mismo criterio que la rama de arriba —una retención deliberada se explica
   * en vez de disparar el aviso genérico—, aplicado a la causa más común de un
   * import con 0 altas: reimportar un fichero ya importado.
   *
   * Medido por la UI el 2026-08-16 con «Reimportación completa» sobre una
   * fuente de Bilaxy. El MISMO aviso decía las dos cosas a la vez:
   *
   *   «La importación finalizó pero creó 0 transacciones. Revisa las filas que
   *    requieren atención o estaban duplicadas.»          ← titular genérico
   *   «↺ Omitidas por duplicado: 2»                       ← y aquí el motivo
   *
   * El titular manda a «Requieren atención», donde no hay nada nuevo que ver.
   * El importador personalizado, ante el mismo desenlace, ya dice la frase
   * exacta; se reutiliza ESA para que las dos superficies no diverjan.
   */
  const soloDuplicados =
    summary.importedCount === 0 &&
    summary.skippedAsDuplicateCount > 0 &&
    summary.unprocessableCount === 0 &&
    summary.needsAttentionCount === 0 &&
    cancelledCount === 0

  return (
    <div className="space-y-1" data-testid="sync-summary-toast-content">
      {zeroImported === true && (
        <div
          className={`flex items-center gap-2 font-medium ${
            withheldOutcome || soloDuplicados ? 'text-crypto-text' : 'text-crypto-warning'
          }`}
          data-testid="sync-summary-zero-imported"
        >
          {/* Nothing landed because every row was withheld on purpose: explain that instead of
              the generic "finished but created 0 transactions" alarm, which sends the user
              looking for data that was never supposed to be imported. */}
          <span aria-hidden="true">{withheldOutcome || soloDuplicados ? '⊘' : '⚠'}</span>
          <span>
            {withheldOutcome
              ? t(withheldOutcome.key, withheldOutcome.params)
              : soloDuplicados
                ? t('customImporter.notifications.importAllDuplicates', {
                    count: summary.skippedAsDuplicateCount,
                  })
                : t('postSyncToast.zeroImportedWarning')}
          </span>
        </div>
      )}
      <div className="flex items-center gap-2" data-testid="sync-summary-imported">
        <span aria-hidden="true">✓</span>
        <span>{t('postSyncToast.imported', { count: summary.importedCount })}</span>
      </div>
      {/* ⚠️ MEDIDO POR LA UI el 2026-08-14. La vista previa del cliente marca la
          fila y explica el motivo —«El importe 0,1234567891 se guardará
          redondeado a 8 decimales»— y el historial de importaciones, en la MISMA
          página, dice «1 fila importada con aviso». Este resumen, que es lo que
          se lee al terminar y lo único que ve quien pulsa de largo, decía sólo
          «✓ Importadas: 2». El dato ya venía en el `summary`; era esta
          superficie la que lo callaba.

          Como «requieren atención», la cifra SOLAPA con «importadas» cuando esas
          filas entran, así que va fuera de la suma y lo declara, o
          «2 importadas + 1 con aviso» sobre un fichero de 2 filas se lee como
          que algo se duplicó.

          ⚠️ MEDIDO POR LA UI el 2026-08-15: el backend cuenta el aviso al
          TRANSFORMAR la fila, antes de deduplicar. En una reimportación donde
          todo resultó duplicado el resumen decía «Importadas: 0 · 2 de ellas
          con un aviso»: dos de cero. Por eso la frase ya no afirma que la fila
          entrase, y el matiz «(ya contadas en «Importadas»)» sólo se muestra
          cuando algo entró de verdad. */}
      {importedWithWarningCount > 0 && (
        <div className="flex items-center gap-2" data-testid="sync-summary-imported-with-warning">
          <span aria-hidden="true">⚠</span>
          <span>{t('postSyncToast.importedWithWarning', { count: importedWithWarningCount })}</span>
          {summary.importedCount > 0 && (
            <span
              className="italic opacity-80"
              data-testid="sync-summary-imported-with-warning-hint"
            >
              {t('postSyncToast.importedWithWarningHint')}
            </span>
          )}
        </div>
      )}
      {/* ⚠️⚠️ MEDIDO POR LA UI el 2026-08-15 con un fichero de DOS filas —una
          válida y otra de importe 0,00—: la vista previa decía «✓ 1 · ∅ 1 Sin
          movimiento» y este resumen, junto al historial, sólo «✓ Importadas: 1».
          La fila desaparecía de todas las superficies que PERMANECEN y la
          invariante que el contrato declara (total = importadas + duplicadas +
          atención + no procesables + canceladas) no cerraba. */}
      {(summary.noMovementCount ?? 0) > 0 && (
        <div className="flex items-center gap-2" data-testid="sync-summary-no-movement">
          <span aria-hidden="true">∅</span>
          <span>{t('postSyncToast.noMovement', { count: summary.noMovementCount ?? 0 })}</span>
        </div>
      )}
      <div className="flex items-center gap-2" data-testid="sync-summary-skipped">
        <span aria-hidden="true">↺</span>
        <span>
          {t('postSyncToast.skippedAsDuplicate', { count: summary.skippedAsDuplicateCount })}
        </span>
      </div>
      {/* ⚠️⚠️ Parte de las omitidas: su registro de origen YA estaba en el libro
          con OTRA clasificación y no se ha tocado. Medido el 2026-09-23: soporte
          restaura la plantilla buena, el cliente reimporta y lee «0 registros»
          con su ingreso de 800 € aún asentado como Retirada. */}
      {(summary.skippedAsReclassifiedDuplicateCount ?? 0) > 0 && (
        <div
          className="flex items-center gap-2 pl-5 text-crypto-warning"
          data-testid="sync-summary-skipped-reclassified"
        >
          <span>
            {t('postSyncToast.skippedAsReclassified', {
              count: summary.skippedAsReclassifiedDuplicateCount ?? 0,
            })}
          </span>
        </div>
      )}
      {/* ⚠️⚠️ LAS 23 FILAS QUE NO TENÍAN CONTADOR EN NINGUNA SUPERFICIE (#2183).
          MEDIDO sobre la importación de una cartera (dirección de Solana): el
          aviso decía «encontradas 95» y el libro se quedó con 72. Las 23
          restantes son movimientos que la cadena registra pero que NO alteran
          ningún saldo de ESTA cartera ni le cobran comisión — un descarte
          legítimo, no una pérdida—, y el backend ya las contaba y tiraba la
          cifra. Sin esta línea, 95 − 72 = 23 filas sin nombre en la única
          superficie que el usuario lee al terminar. */}
      {(summary.noWalletBalanceEffectCount ?? 0) > 0 && (
        <div
          className="flex items-center gap-2"
          data-testid="sync-summary-no-wallet-balance-effect"
        >
          <span aria-hidden="true">⊙</span>
          <span>
            {t('postSyncToast.noWalletBalanceEffect', {
              count: summary.noWalletBalanceEffectCount ?? 0,
            })}
          </span>
        </div>
      )}
      {/* ⚠️⚠️ EL TERCER DESTINO (#2138). MEDIDO EN LA BASE DE DATOS el
          2026-09-05 sobre una importación real de HyperLiquid: de 20.168 filas
          leídas, 3.350 generaron asiento y 9.242 eran duplicadas. Las 7.576
          restantes eran aperturas de perpetuo —que abren posición y no son un
          hecho realizado (#2128)— y NO tenían contador: el resumen las sumaba a
          las «correctas» y declaraba 20.168 importadas y 0 omitidas.
          Lleva su MOTIVO en el propio texto: un número sin porqué obliga al
          usuario a inventarse la explicación. */}
      {(summary.ignoredByRuleCount ?? 0) > 0 && (
        <div className="flex flex-col gap-0.5">
          <div className="flex items-center gap-2" data-testid="sync-summary-ignored-by-rule">
            <span aria-hidden="true">⊘</span>
            <span>
              {t('postSyncToast.ignoredByRule', { count: summary.ignoredByRuleCount ?? 0 })}
            </span>
          </div>
          {/* ⚠️⚠️ EL DESGLOSE, Y VA A LA VISTA (#2186). El agregado de arriba
              cerraba la aritmética —13.578 + 1.366 + 8.066 = 23.010 exacto— y
              aun así no permitía distinguir «ese endpoint no devolvió nada» de
              «devolvió y se descartó». Por eso NO va detrás de un desplegable
              como el de conciliación: el dato que resuelve la duda es
              precisamente el CERO junto a un endpoint, y un cero escondido tras
              un clic sigue pareciéndose a la otra situación.

              Cada línea lleva su DENOMINADOR («de N leídas») y el nombre de la
              regla que descartó: un contador sin su porqué obliga al lector a
              inventarse la explicación, que es el defecto que esto arregla. */}
          {ignoredByRuleByEndpoint.length > 0 && (
            <ul className="ml-6 space-y-0.5" data-testid="sync-summary-ignored-by-rule-breakdown">
              {ignoredByRuleByEndpoint.map((origen) => (
                <li
                  key={origen.endpointKey}
                  className="font-mono text-xs"
                  data-testid={`sync-summary-ignored-by-rule-row-${origen.endpointKey}`}
                >
                  {/* ⚠️ Cifras SIN formato de miles, igual que las seis líneas
                      de arriba de este mismo aviso (`{{count}}` crudo). Darles
                      aquí un formato que sus hermanas no tienen haría que el
                      mismo 8066 se leyera de dos maneras a tres líneas de
                      distancia. */}
                  {t('postSyncToast.ignoredByRuleRow', {
                    endpoint: origen.endpointKey,
                    ignored: origen.ignoredCount,
                    read: origen.rowsRead,
                    // Los nombres de regla son DATO del catálogo, no copia: se
                    // pintan tal cual. Un endpoint sin reglas con nombre deja
                    // este hueco vacío en vez de enseñar un rótulo inventado.
                    reasons: origen.reasons
                      .map((motivo) => `· ${motivo.reason} (${motivo.count})`)
                      .join(' '),
                  }).trim()}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      {summary.needsAttentionCount > 0 && (
        <div className="flex items-center gap-2" data-testid="sync-summary-needs-attention">
          <span aria-hidden="true">⚠</span>
          <span>{t('postSyncToast.needsAttention', { count: summary.needsAttentionCount })}</span>
          <button
            type="button"
            onClick={onViewTray}
            className="underline font-medium hover:text-crypto-accent transition-colors"
            data-testid="sync-summary-needs-attention-link"
          >
            {t('postSyncToast.viewTray')}
          </button>
          {/* La ÚNICA cifra del aviso que es un corte transversal: una fila en
              la bandeja ya está contada arriba como importada o como no
              procesable (`buildNeedsAttentionMembershipPredicate` admite las
              dos formas). Sin decirlo, «5 importadas + 1 requieren atención +
              1 no procesable» sobre un fichero de 6 filas se lee como que algo
              se perdió o se duplicó. Sus dos hermanas de este mismo aviso ya
              declaran lo suyo; esta era la que callaba. */}
          <span className="italic opacity-80" data-testid="sync-summary-needs-attention-hint">
            {t('postSyncToast.needsAttentionHint')}
          </span>
        </div>
      )}
      {cancelledCount > 0 && (
        <div className="flex items-center gap-2" data-testid="sync-summary-cancelled">
          {/* Deliberately NOT the ✗ the unprocessable line uses: these rows did not fail. */}
          <span aria-hidden="true">⊘</span>
          <span>{t('postSyncToast.cancelled', { count: cancelledCount })}</span>
          <span className="italic opacity-80" data-testid="sync-summary-cancelled-hint">
            {t('postSyncToast.cancelledHint')}
          </span>
        </div>
      )}
      {/* Rows the importer held back (#3064): they move several assets on one side, are
          out of the gains calculation until reviewed, and are NOT unprocessable. Optional in
          the contract (jobs closed before it omit it): absent reads as none. */}
      {(summary.heldForReviewCount ?? 0) > 0 && (
        <div
          className="flex items-center gap-2"
          role="status"
          data-testid="sync-summary-held-for-review"
        >
          <span aria-hidden="true">⚠</span>
          <span>{t('postSyncToast.heldForReview', { count: summary.heldForReviewCount })}</span>
        </div>
      )}
      {summary.unprocessableCount > 0 && (
        <div className="flex items-center gap-2" data-testid="sync-summary-unprocessable">
          <span aria-hidden="true">✗</span>
          <span>{t('postSyncToast.unprocessable', { count: summary.unprocessableCount })}</span>
          <span className="italic opacity-80" data-testid="sync-summary-unprocessable-hint">
            {t('postSyncToast.unprocessableHint')}
          </span>
        </div>
      )}
      {/* ⚠️⚠️ Manda el VEREDICTO del servicio, no un recuento paralelo.
          El backend ya decide si los descuadres importan
          (`determineReconciliationStatus`: crítico → failure, alto o más de
          cinco medios → warning, el resto → success) y esta línea volvía a
          decidirlo por su cuenta con `discrepancyCount > 0`. Resultado: una
          reconciliación que el propio servicio da por BUENA pintaba igualmente
          «⚖ N descuadres» al usuario.
          MEDIDO POR LA UI el 2026-08-14, importando 0,00012345 BTC:
          `{"status":"success","discrepancyCount":1,"severity":"low"}`.
          El desglose sigue disponible cuando SÍ hay problema, y el detalle
          completo vive en el historial del trabajo. */}
      {summary.reconciliation &&
        summary.reconciliation.status !== 'success' &&
        summary.reconciliation.discrepancyCount > 0 && (
          <div className="flex items-center gap-2" data-testid="sync-summary-reconciliation">
            <span aria-hidden="true">⚖</span>
            <span>
              {t('postSyncToast.reconciliationDiscrepancies', {
                count: summary.reconciliation.discrepancyCount,
              })}
            </span>
            {/* Native <details> keeps the per-asset breakdown viewable without
              bloating the toast — the count is always visible, expanding it
              reveals one row per discrepant asset. */}
            <details data-testid="sync-summary-reconciliation-details">
              <summary className="cursor-pointer underline hover:text-crypto-accent transition-colors">
                {t('postSyncToast.reconciliationViewBreakdown')}
              </summary>
              <ul className="mt-1 ml-4 list-disc space-y-0.5">
                {summary.reconciliation.discrepancies.map((discrepancy) => (
                  <li
                    key={discrepancy.symbol}
                    className="font-mono text-xs"
                    data-testid={`sync-summary-reconciliation-row-${discrepancy.symbol}`}
                  >
                    {t('postSyncToast.reconciliationRow', {
                      symbol: discrepancy.symbol,
                      expected: discrepancy.expectedBalance,
                      actual: discrepancy.actualBalance,
                      severity: discrepancy.severity,
                    })}
                  </li>
                ))}
              </ul>
            </details>
          </div>
        )}
      {/* ⚠️⚠️ La API no entregó todo el periodo pedido: el proveedor sólo
          conserva sus N registros más recientes. Medido el 2026-09-14 en
          HyperLiquid (importar desde el 12 → fills sólo desde el 14 16:22) con
          este aviso en verde. */}
      {summary.chainHistoryIncomplete && (
        <ChainHistoryIncompleteNotice
          gaps={summary.chainHistoryIncomplete}
          testId="sync-summary-chain-history-incomplete"
        />
      )}
      {(summary.coverageGaps?.length ?? 0) > 0 && (
        <ImportCoverageGapNotice
          gaps={summary.coverageGaps ?? []}
          testId="sync-summary-coverage-gap"
        />
      )}
      {/* ⚠️⚠️ Otra fuente del mismo medio ya cubre estas fechas (API frente a
          CSV de la misma cuenta): contadas dos veces sin que la deduplicación
          pueda verlo. */}
      {summary.crossSourceOverlap && (
        <ImportSourceOverlapNotice
          overlap={summary.crossSourceOverlap}
          variant="after-import"
          testId="sync-summary-cross-source-overlap"
        />
      )}
    </div>
  )
}
